import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';

const RequestStonePage = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    stone_name: '',
    size_carat: '',
    budget_range: '',
    other_details: '',
    phone: user?.phone || ''
  });

  useEffect(() => {
    if (!isAuthenticated()) {
      toast.error('Please sign in to request a stone');
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (user?.phone) {
      setFormData(prev => ({ ...prev, phone: user.phone }));
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/stone-requests', formData);
      toast.success('Stone request submitted! We will contact you shortly.');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated()) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <Sparkles className="w-16 h-16 mx-auto mb-6 text-yellow-500" />
          <h1 className="font-serif text-4xl md:text-5xl mb-4">Request a Gemstone</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Can't find what you're looking for? Tell us about your dream gemstone and we'll source it for you.
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="stone_name">Gemstone Type *</Label>
              <Input
                id="stone_name"
                name="stone_name"
                value={formData.stone_name}
                onChange={handleChange}
                placeholder="e.g., Emerald, Ruby, Sapphire, Diamond"
                required
              />
              <p className="text-sm text-gray-500 mt-1">Which gemstone are you looking for?</p>
            </div>

            <div>
              <Label htmlFor="size_carat">Desired Size / Carat Weight *</Label>
              <Input
                id="size_carat"
                name="size_carat"
                value={formData.size_carat}
                onChange={handleChange}
                placeholder="e.g., 2-3 carats, 10mm round, 8x6mm oval"
                required
              />
              <p className="text-sm text-gray-500 mt-1">Approximate size or carat weight</p>
            </div>

            <div>
              <Label htmlFor="budget_range">Budget Range *</Label>
              <Input
                id="budget_range"
                name="budget_range"
                value={formData.budget_range}
                onChange={handleChange}
                placeholder="e.g., $5,000 - $10,000, £3,000 - £7,000"
                required
              />
              <p className="text-sm text-gray-500 mt-1">Your budget helps us find the perfect match</p>
            </div>

            <div>
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Your phone number"
                required
              />
            </div>

            <div>
              <Label htmlFor="other_details">Additional Details</Label>
              <Textarea
                id="other_details"
                name="other_details"
                value={formData.other_details}
                onChange={handleChange}
                placeholder="Color preferences, cut preferences (round, oval, emerald cut), origin preferences, certification requirements, intended use (ring, pendant, loose stone), or any other specific requirements..."
                rows={6}
              />
              <p className="text-sm text-gray-500 mt-1">The more details you provide, the better we can help</p>
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-black hover:bg-gray-800"
              >
                {loading ? 'Submitting...' : 'Submit Request'}
              </Button>
            </div>
          </form>
        </div>

        {/* Info Section */}
        <div className="mt-8 text-center text-gray-600">
          <p className="text-sm">
            Our gemstone experts will review your request and reach out within 24-48 hours with available options.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RequestStonePage;
